import React, { useState } from 'react';
import {
  Box, Typography, Paper, Chip, IconButton, TextField, Tooltip,
  Stack, Avatar, Button, LinearProgress, InputAdornment,
} from '@mui/material';
import {
  Pause as PauseIcon, Stop as StopIcon, Chat as ChatIcon,
  Send as SendIcon, CheckCircle as ApproveIcon, Cancel as RejectIcon,
  PanTool as TakeoverIcon, Extension as ExtensionIcon,
  Settings as SettingsIcon, AutoAwesome as AiIcon,
  SmartToy as BotIcon, Person as PersonIcon,
  Visibility as EyeIcon, Bolt as BoltIcon,
  Circle as DotIcon, MoreVert as MoreIcon,
} from '@mui/icons-material';
import { STUDENT, CLASS, CLIENT } from '../sharedMockData';

/**
 * Ext Admin — Agent Mode
 *
 * Staff / developer-facing surface. End-users do NOT have this.
 * Claude is driving the browser. Shows:
 *  - Agent reasoning stream (scrollable thought log)
 *  - Approval queue (pending proposed step, accept/reject)
 *  - Chat input at bottom for course-corrections
 *  - Pause / Takeover / Stop controls
 *  - References Watch Mode Channel (frameRef) — agent watches the target tab
 *    through a CDP frame reference similar to Watch Mode.
 */

const SIDEBAR_WIDTH = 400;
const PURPLE = '#7b1fa2';
const DEEP_PURPLE = '#4a148c';

const REASONING = [
  {
    role: 'agent',
    at: '09:14:02',
    text: `Goal: roster student "${STUDENT.full}" in iCivics class ${CLASS.section}. Looking for roster button…`,
  },
  {
    role: 'agent',
    at: '09:14:05',
    text: 'Found "Class Members" link in nav. Clicking to enter member management.',
    action: 'click nav a[href*="/members"]',
  },
  {
    role: 'user',
    at: '09:14:12',
    text: `Make sure we pass the @${CLIENT.domain} email domain, not the default`,
  },
  {
    role: 'agent',
    at: '09:14:14',
    text: `Acknowledged. I'll construct "${STUDENT.email}" for the signup form.`,
  },
  {
    role: 'agent',
    at: '09:14:20',
    text: `Member list loaded (12 rows). ${STUDENT.full} not present — proceeding to "Add Member" flow.`,
  },
];

const PROPOSED_STEP = {
  type: 'click',
  name: 'Click "Add Member" button',
  selector: 'button[data-testid="add-member-btn"]',
  rationale: 'Only primary-action button on Class Members page. data-testid is stable.',
  confidence: 0.94,
};

const ExtAdminAgentMockup = () => {
  const [chatValue, setChatValue] = useState('');

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', bgcolor: '#eef1f5', p: 3, minHeight: 'calc(100vh - 180px)' }}>
      <Paper elevation={6} sx={{
        width: SIDEBAR_WIDTH, display: 'flex', flexDirection: 'column',
        bgcolor: '#fff', overflow: 'hidden',
        border: '1px solid #d5d9df', maxHeight: 'calc(100vh - 200px)',
      }}>
        {/* Toolbar with agent-mode accent */}
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 1,
          background: `linear-gradient(90deg, ${DEEP_PURPLE} 0%, ${PURPLE} 100%)`,
          color: '#fff',
        }}>
          <ExtensionIcon sx={{ fontSize: 20 }} />
          <Typography sx={{ fontWeight: 700, fontSize: '0.9rem' }}>Automatrix</Typography>
          <Chip
            icon={<AiIcon sx={{ fontSize: 12, color: '#fff !important' }} />}
            label="Agent Mode"
            size="small"
            sx={{
              height: 20, fontSize: '0.65rem', fontWeight: 700,
              bgcolor: 'rgba(255,255,255,0.22)', color: '#fff',
            }}
          />
          <Box sx={{ flexGrow: 1 }} />
          <IconButton size="small" sx={{ color: '#fff' }}>
            <SettingsIcon sx={{ fontSize: 18 }} />
          </IconButton>
          <IconButton size="small" sx={{ color: '#fff' }}>
            <MoreIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>

        {/* Frame ref / Watch Mode channel indicator */}
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 0.75,
          bgcolor: '#ede7f6', borderBottom: '1px solid #d1c4e9',
        }}>
          <DotIcon sx={{ fontSize: 10, color: '#66bb6a' }} />
          <Typography variant="caption" sx={{ fontSize: '0.68rem', color: '#4527a0', fontWeight: 600 }}>
            frameRef: tab#817 · iCivics teacher portal
          </Typography>
          <Box sx={{ flexGrow: 1 }} />
          <Tooltip title="Open Watch Mode side-by-side viewer">
            <IconButton size="small" sx={{ p: 0.25, color: PURPLE }}>
              <EyeIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Goal card */}
        <Box sx={{ px: 1.5, py: 1, bgcolor: '#faf7fd', borderBottom: '1px solid #eceff2' }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <AiIcon sx={{ fontSize: 16, color: PURPLE }} />
            <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.7rem', color: '#4a148c', textTransform: 'uppercase' }}>
              Agent Goal
            </Typography>
          </Stack>
          <Typography variant="body2" sx={{ fontSize: '0.82rem', mt: 0.5, color: '#263238' }}>
            Add student <b>{STUDENT.full}</b> to iCivics class {CLASS.section} @ {CLIENT.domain.split('.')[0]}
          </Typography>
          <Box sx={{ mt: 0.75 }}>
            <LinearProgress
              variant="determinate" value={42}
              sx={{
                height: 4, borderRadius: 1, bgcolor: '#e1bee7',
                '& .MuiLinearProgress-bar': { bgcolor: PURPLE },
              }}
            />
            <Stack direction="row" justifyContent="space-between" sx={{ mt: 0.5 }}>
              <Typography variant="caption" sx={{ fontSize: '0.64rem', color: '#7e57c2' }}>
                step 4 of ~9
              </Typography>
              <Typography variant="caption" sx={{ fontSize: '0.64rem', color: '#7e57c2', fontFamily: 'monospace' }}>
                claude-sonnet-4-6
              </Typography>
            </Stack>
          </Box>
        </Box>

        {/* Reasoning stream */}
        <Box sx={{ flexGrow: 1, overflow: 'auto', px: 1, py: 1, bgcolor: '#fafafa' }}>
          {REASONING.map((entry, i) => (
            <Stack
              key={i}
              direction="row"
              spacing={1}
              sx={{
                mb: 1, alignItems: 'flex-start',
              }}
            >
              <Avatar sx={{
                width: 22, height: 22, fontSize: 12,
                bgcolor: entry.role === 'agent' ? PURPLE : '#1976d2',
              }}>
                {entry.role === 'agent' ? <BotIcon sx={{ fontSize: 14 }} /> : <PersonIcon sx={{ fontSize: 14 }} />}
              </Avatar>
              <Box sx={{
                flexGrow: 1,
                bgcolor: entry.role === 'agent' ? '#fff' : '#e3f2fd',
                border: '1px solid',
                borderColor: entry.role === 'agent' ? '#eceff2' : '#bbdefb',
                borderRadius: 1, px: 1, py: 0.6,
              }}>
                <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.25 }}>
                  <Typography variant="caption" sx={{
                    fontWeight: 700, fontSize: '0.65rem',
                    color: entry.role === 'agent' ? PURPLE : '#1565c0',
                  }}>
                    {entry.role === 'agent' ? 'Claude' : 'You'}
                  </Typography>
                  <Typography variant="caption" sx={{ fontSize: '0.62rem', color: '#90a4ae', fontFamily: 'monospace' }}>
                    {entry.at}
                  </Typography>
                </Stack>
                <Typography variant="body2" sx={{ fontSize: '0.77rem', color: '#37474f', lineHeight: 1.35 }}>
                  {entry.text}
                </Typography>
                {entry.action && (
                  <Box sx={{
                    mt: 0.5, px: 0.75, py: 0.3, bgcolor: '#263238',
                    color: '#a5d6a7', fontSize: '0.65rem', fontFamily: 'monospace',
                    borderRadius: 0.5, display: 'inline-block',
                  }}>
                    {entry.action}
                  </Box>
                )}
              </Box>
            </Stack>
          ))}

          {/* Pending / thinking indicator */}
          <Stack direction="row" spacing={1} sx={{ mt: 0.5, alignItems: 'center', ml: 3.5 }}>
            <BoltIcon sx={{ fontSize: 14, color: PURPLE }} />
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: PURPLE, fontStyle: 'italic' }}>
              Claude is proposing next step…
            </Typography>
          </Stack>
        </Box>

        {/* Approval queue */}
        <Box sx={{
          borderTop: '2px solid #ffc107',
          bgcolor: '#fffde7', px: 1.5, py: 1,
        }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
            <Typography variant="caption" sx={{
              fontWeight: 700, fontSize: '0.68rem', color: '#f57f17', textTransform: 'uppercase',
            }}>
              Approval needed
            </Typography>
            <Chip
              label={`${Math.round(PROPOSED_STEP.confidence * 100)}% confidence`}
              size="small"
              sx={{ height: 16, fontSize: '0.6rem', bgcolor: '#fff', color: '#f57f17', fontWeight: 700 }}
            />
            <Box sx={{ flexGrow: 1 }} />
            <Typography variant="caption" sx={{ fontSize: '0.62rem', color: '#8d6e63', fontFamily: 'monospace' }}>
              auto-run in 8s
            </Typography>
          </Stack>
          <Box sx={{
            bgcolor: '#fff', border: '1px solid #ffe082', borderRadius: 1, p: 1, mb: 0.75,
          }}>
            <Stack direction="row" spacing={0.75} alignItems="center">
              <Chip label={PROPOSED_STEP.type} size="small" sx={{
                height: 16, fontSize: '0.58rem', bgcolor: '#e3f2fd', color: '#1565c0', fontWeight: 700,
              }} />
              <Typography variant="body2" sx={{ fontSize: '0.78rem', fontWeight: 600, flexGrow: 1 }}>
                {PROPOSED_STEP.name}
              </Typography>
            </Stack>
            <Typography variant="caption" sx={{
              display: 'block', fontSize: '0.65rem', fontFamily: 'monospace',
              color: '#546e7a', mt: 0.3,
            }}>
              {PROPOSED_STEP.selector}
            </Typography>
            <Typography variant="caption" sx={{
              display: 'block', fontSize: '0.68rem', color: '#8d6e63', mt: 0.3, fontStyle: 'italic',
            }}>
              {PROPOSED_STEP.rationale}
            </Typography>
          </Box>
          <Stack direction="row" spacing={0.75}>
            <Button
              size="small" variant="contained" color="success"
              startIcon={<ApproveIcon sx={{ fontSize: 14 }} />}
              sx={{ textTransform: 'none', fontSize: '0.72rem', py: 0.25, flexGrow: 1 }}
            >
              Approve
            </Button>
            <Button
              size="small" variant="outlined" color="error"
              startIcon={<RejectIcon sx={{ fontSize: 14 }} />}
              sx={{ textTransform: 'none', fontSize: '0.72rem', py: 0.25 }}
            >
              Reject
            </Button>
            <Button
              size="small" variant="outlined"
              sx={{ textTransform: 'none', fontSize: '0.72rem', py: 0.25 }}
            >
              Edit
            </Button>
          </Stack>
        </Box>

        {/* Chat input */}
        <Box sx={{
          borderTop: '1px solid #eceff2', bgcolor: '#fff', px: 1, py: 0.75,
        }}>
          <TextField
            size="small" fullWidth multiline maxRows={3}
            placeholder="Nudge Claude (e.g. ‘use class code ABC123 instead’)…"
            value={chatValue}
            onChange={(e) => setChatValue(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <ChatIcon sx={{ fontSize: 16, color: '#90a4ae' }} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton size="small" sx={{ color: PURPLE }}>
                    <SendIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </InputAdornment>
              ),
              sx: { fontSize: '0.8rem', bgcolor: '#f7f9fb' },
            }}
          />
        </Box>

        {/* Agent control bar (Pause / Takeover / Stop) */}
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.75,
          borderTop: '1px solid #eceff2', bgcolor: '#faf7fd',
        }}>
          <Tooltip title="Pause agent (take a moment)">
            <IconButton size="small" sx={{ bgcolor: '#fff3e0', color: '#e65100', width: 28, height: 28 }}>
              <PauseIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Takeover (drive manually, agent watches)">
            <Button
              size="small" variant="outlined"
              startIcon={<TakeoverIcon sx={{ fontSize: 14 }} />}
              sx={{
                textTransform: 'none', fontSize: '0.7rem', py: 0.15,
                borderColor: PURPLE, color: PURPLE,
              }}
            >
              Takeover
            </Button>
          </Tooltip>
          <Box sx={{ flexGrow: 1 }} />
          <Tooltip title="Stop agent">
            <IconButton size="small" sx={{ bgcolor: '#ffebee', color: '#c62828', width: 28, height: 28 }}>
              <StopIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Paper>
    </Box>
  );
};

export default ExtAdminAgentMockup;
